import { connect, type ConnectedProps } from 'react-redux';
import {
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { useFormik } from 'formik';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import {
  Footer,
  FormContainer,
  MainContainer,
  PhoneInput,
  TopHeaderBackArrow,
} from '~/components/utility';
import { type RootState } from '~/reducers';
import { forgotPassword } from '~/services/verify';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';

const mapStateToProps = ({
  env: { deviceId, osType, osVersion, appVersion },
  nav: { query },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  query,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'forgot-password';

const ForgotPassword = ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  query,
}: Props) => {
  const validationSchema = z.object({
    phone: z
      .string(
        i18n.t(
          'ForgotPasswordPhone.content.forgotPasswordForm.formField.phone.errors.required',
        ),
      )
      .refine(
        (value) => parsePhoneNumberFromString(value)?.isPossible() ?? false,
        {
          error: i18n.t(
            'ForgotPasswordPhone.content.forgotPasswordForm.formField.phone.errors.phoneNumberValid',
          ),
        },
      ),
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    dirty,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik({
    initialValues: {
      phone: '',
    },
    validationSchema: toFormikValidationSchema(validationSchema),
    validateOnBlur: true,
    onSubmit: async (formValues) => {
      const { data } = await forgotPassword({
        phone: formValues.phone,
        osType,
        osVersion,
        appVersion,
        deviceId,
      });

      notifyApp('adc-webview:forgot-password', data);

      mediator.publish('router:navigate', {
        url: '/verify-phone',
        query: {
          ...query,
          phone: formValues.phone,
          reset: true,
        },
      });
    },
  });

  const handlePressBack = () => {
    mediator.publish('router:back');
  };

  return (
    <MainContainer testID={testID}>
      {isSubmitting && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}

      <InformationalScreen
        testID={buildTestId({ testID, suffix: 'screen' })}
        topNavigationProps={{
          animatedTitle: true,
          title: i18n.t('ForgotPasswordPhone.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="nav-back" />,
            label: 'Go Back',
            onPress: handlePressBack,
          },
          testID: 'nav-back',
        }}
      >
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
        >
          <InformationalScreen.Content>
            <Text marginTop={-8}>{i18n.t('ForgotPasswordPhone.subtitle')}</Text>
            <Stack marginTop="$11">
              <FormContainer hasHiddenUserInput>
                <PhoneInput
                  testID={buildTestId({ testID, suffix: ['phone', 'input'] })}
                  label={i18n.t(
                    'ForgotPasswordPhone.content.forgotPasswordForm.formField.phone.label',
                  )}
                  placeholder={i18n.t(
                    'ForgotPasswordPhone.content.forgotPasswordForm.formField.phone.placeholder',
                  )}
                  value={values.phone}
                  onChangeText={handleChange('phone')}
                  onBlur={handleBlur('phone')}
                  isInvalid={Boolean(errors.phone && touched.phone)}
                  errorMessage={errors.phone}
                />
              </FormContainer>
            </Stack>
          </InformationalScreen.Content>

          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t(
                'ForgotPasswordPhone.content.forgotPasswordForm.links.send.label',
              )}
              onButtonPress={handleSubmit}
              buttonTestID={buildTestId({
                testID,
                suffix: ['submit', 'button'],
              })}
              disabled={!isValid || !dirty || isSubmitting}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(ForgotPassword);
